import { type ReactNode, type JSX } from 'react';
import { FallbackProps } from 'react-error-boundary';
import { FormattedMessage } from 'react-intl';

import { Flex } from '@dynatrace/strato-components/layouts';
import { Text } from '@dynatrace/strato-components/typography';
import { CriticalIcon } from '@dynatrace/strato-icons';

import { errorText, wrapper } from './ChartState.sty.js';

/**
 * @internal
 */
export type ErrorStateProps = {
  height: string | number;
  children?: ReactNode | ((message: string) => JSX.Element);
};

const ErrorStateTextWrapper = ({ height, children }: ErrorStateProps) => (
  <Flex
    justifyContent="center"
    alignItems="center"
    height={height}
    width={'100%'}
  >
    <Text className={errorText} textStyle="base">
      {children as ReactNode}
    </Text>
  </Flex>
);

/**
 * Component used in charts to display an error message. To be used with HOC withErrorBoundary() as a fallback component
 *
 * @example
 * ```
 * const ErrorBoundedChart = withErrorBoundary(Chart, { FallbackComponent: ErrorState({ height })});
 * ```
 * @internal
 */
export const ErrorState =
  ({ height, children }: ErrorStateProps) =>
  (fallbackProps: FallbackProps) => {
    let errorNode: ReactNode;

    if (children && typeof children === 'function') {
      errorNode = children(fallbackProps.error.message);
    } else {
      const errorMsg = fallbackProps.error.message
        ? `: ${fallbackProps.error.message}`
        : '';

      errorNode = children || (
        <Flex className={wrapper}>
          <CriticalIcon />
          <FormattedMessage
            id="9SDB/Tz9eGlVpihz"
            defaultMessage="An error occurred"
            description="Message to display when there is an error"
          />
          {errorMsg}
        </Flex>
      );
    }

    return (
      <ErrorStateTextWrapper height={height}>{errorNode}</ErrorStateTextWrapper>
    );
  };

ErrorState['displayName'] = 'ErrorState';
